import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';


const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        
        const { accessToken, refreshToken: rotatedRefreshToken } = response.data;
        localStorage.setItem('accessToken', accessToken);
        if (rotatedRefreshToken) {
          localStorage.setItem('refreshToken', rotatedRefreshToken);
        }
        
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Preserve the current page (e.g. an NCF email link) so login can return here, mirroring PrivateRoute.
        const returnPath = `${window.location.pathname}${window.location.search}`;
        if (returnPath && returnPath !== '/login') {
          sessionStorage.setItem('postLoginRedirect', returnPath);
        }
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: () => api.get('/auth/login'),
  callback: (code) => api.post('/auth/callback', { code }),
  getProfile: () => api.get('/auth/me')
};

export { api, publicApi };

// Contacts API
export const contactsAPI = {
  getAll: () => api.get('/contacts'),
  getFolders: () => api.get('/contacts/folders'),
  sync: (data) => api.post('/contacts/sync', data),
  normalizeNames: () => api.post('/contacts/normalize-names'),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  bulkDelete: () => api.delete('/contacts'),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/contacts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/contacts/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importConfirm: (contacts) => api.post('/contacts/import/confirm', { contacts })
};

// Contact Lists API
export const contactListsAPI = {
  getAll: () => api.get('/contact-lists'),
  getOne: (id) => api.get(`/contact-lists/${id}`),
  create: (data) => api.post('/contact-lists', data),
  update: (id, data) => api.put(`/contact-lists/${id}`, data),
  delete: (id) => api.delete(`/contact-lists/${id}`),
  addContacts: (id, contactIds) => api.post(`/contact-lists/${id}/contacts`, { contactIds }),
  removeContact: (id, contactId) => api.delete(`/contact-lists/${id}/contacts/${contactId}`)
};

// Templates API
export const templatesAPI = {
  getAll: () => api.get('/templates'),
  getOne: (id) => api.get(`/templates/${id}`),
  listDesignImages: () => api.get('/templates/design-images'),
  uploadDesignImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/templates/design-images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDesignImage: (imageId, data) => api.patch(`/templates/design-images/${imageId}`, data),
  deleteDesignImageById: (imageId) => api.delete(`/templates/design-images/${imageId}`),
  transformDesignImage: (imageId, data) => api.post(`/templates/design-images/${imageId}/transform`, data),
  listDesignImageVariants: (imageId) => api.get(`/templates/design-images/${imageId}/variants`),
  getCuratedUnlayerTemplates: () => api.get('/unlayer/curated'),
  getUnlayerTemplates: () => api.get('/unlayer/templates'),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  preview: (id, sampleData) => api.post(`/templates/${id}/preview`, { sampleData }),
  validatePlaceholders: (id) => api.post(`/templates/${id}/validate-placeholders`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/templates/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  aiAssist: (data) => api.post('/templates/ai-assist', data),
  aiDesign: (data) => api.post('/templates/ai-design', data),
  deleteImage: (imageUrl) => api.delete('/templates/image', { data: { imageUrl } })
};

// Campaigns API
export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  getOne: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  addManualRecipients: (data) => api.post('/campaigns/manual-recipients', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  send: (id) => api.post(`/campaigns/${id}/send`),
  stop: (id) => api.post(`/campaigns/${id}/stop`),
  getLogs: (id) => api.get(`/campaigns/${id}/logs`),
  getContacts: (id) => api.get(`/campaigns/${id}/contacts`),
  delete: (id) => api.delete(`/campaigns/${id}`)
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getCampaignStats: (id) => api.get(`/analytics/campaign/${id}`),
  trackClick: (campaignId, contactId) => 
    api.post('/analytics/click', { campaignId, contactId })
};

// Queue API
export const queueAPI = {
  clear: () => api.delete('/queue/clear')
};

// Rate Calculator API
export const rateCalculatorAPI = {
  getClients: () => api.get('/rate-calculator/clients'),
  getDiscounts: () => api.get('/rate-calculator/discounts')
};

export const videoBackgroundsAPI = {
  getPresets: () => api.get('/video-backgrounds/presets'),
  getLogos: () => api.get('/video-backgrounds/logos'),
  getMine: () => api.get('/video-backgrounds/mine'),
  getShared: () => api.get('/video-backgrounds/shared'),
  getSettings: () => api.get('/video-backgrounds/settings'),
  setGeneratorVisibility: (enabled) => api.put('/video-backgrounds/settings/generator-visibility', { enabled }),
  health: (probe = false) => api.get(`/video-backgrounds/health${probe ? '?probe=true' : ''}`),
  deleteMine: (blobName) => api.delete('/video-backgrounds/mine', { data: { blobName } }),
  deleteShared: (blobName) => api.delete('/video-backgrounds/shared', { data: { blobName } }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/video-backgrounds/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadShared: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/video-backgrounds/shared/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  applyLogo: (file, payload) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('logoKey', payload?.logoKey || 'RSC');
    formData.append('placementPreset', payload?.placementPreset || 'standard-logo');
    return api.post('/video-backgrounds/apply-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  generate: (payload) => api.post('/video-backgrounds/generate', payload)
};

// NCF (New Consultant Form) API
export const ncfAPI = {
  list: (params = {}) => api.get('/ncf', { params }),
  get: (id) => api.get(`/ncf/${id}`),
  fields: () => api.get('/ncf/fields'),
  reviewers: () => api.get('/ncf/reviewers'),
  reviewerDefaults: (id) => api.get(`/ncf/reviewers/defaults/${id}`),
  hrNotificationRecipients: () => api.get('/ncf/hr-notification-recipients'),
  myPendingApprovals: () => api.get('/ncf/my-pending-approvals'),
  myPendingStartDates: () => api.get('/ncf/my-pending-start-dates'),
  adminFields: () => api.get('/ncf/fields/admin'),
  rebuildNcfRoleAssignments: (payload) => api.post('/ncf/role-assignments/rebuild', payload),
  updateSectionVisibility: (payload) => api.put('/ncf/section-visibility', payload),
  submissionSettings: () => api.get('/ncf/submission-settings'),
  saveSubmissionSettings: (payload) => api.put('/ncf/submission-settings', payload),
  runStartDateReminders: () => api.post('/ncf/submission-settings/start-date-reminders/run-now'),
  searchSubmissionDirectory: (q, config = {}) => api.get('/ncf/submission-settings/directory', { ...config, params: { q } }),
  searchRecipientDirectory: (q, config = {}) => api.get('/ncf/directory', { ...config, params: { q } }),
  createField: (payload) => api.post('/ncf/fields', payload),
  updateFieldDefinition: (fieldId, payload) => api.put(`/ncf/fields/${fieldId}`, payload),
  removeField: (fieldId) => api.delete(`/ncf/fields/${fieldId}`),
  statuses: () => api.get('/ncf/statuses'),
  adminStatuses: () => api.get('/ncf/statuses/admin'),
  createStatus: (payload) => api.post('/ncf/statuses', payload),
  updateStatusDefinition: (statusId, payload) => api.put(`/ncf/statuses/${statusId}`, payload),
  removeStatus: (statusId) => api.delete(`/ncf/statuses/${statusId}`),
  create: (formData) => api.post('/ncf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  saveReview: (id, reviewedData) => api.put(`/ncf/${id}/review`, { reviewedData }),
  submitToHr: (id, financialReview, submitterComment = '') => api.post(`/ncf/${id}/submit`, { financialReview, submitterComment }),
  approval: (id) => api.get(`/ncf/${id}/approval`),
  requestApproval: (id, approvals, additionalRecipients = [], customMessage = '', reviewNotRequired = false) => api.post(`/ncf/${id}/approval/request`, { approvals, additionalRecipients, customMessage, reviewNotRequired }),
  decideApproval: (id, role, decision, note) => api.post(`/ncf/${id}/approval/${role}`, { decision, note }),
  overrideApproval: (id, reason, financialReview, submitterComment = '') => api.post(`/ncf/${id}/approval/override`, { reason, financialReview, submitterComment }),
  submissions: (id) => api.get(`/ncf/${id}/submissions`),
  notificationLogs: (id) => api.get(`/ncf/${id}/notification-logs`),
  comments: (id) => api.get(`/ncf/${id}/comments`),
  addComment: (id, payload) => api.post(`/ncf/${id}/comments`, payload),
  changeReasons: (permanent = false) => api.get('/ncf/change-reasons', { params: { permanent } }),
  suggestAddresses: (q, config = {}) => api.get('/ncf/address/suggest', { ...config, params: { q } }),
  validateAddresses: (addresses) => api.post('/ncf/address/validate', { addresses }),
  getJobChange: (id, jobId, config = {}) => api.get(`/ncf/${id}/updates/job/${jobId}`, config),
  previewChange: (id, payload) => api.post(`/ncf/${id}/updates/preview`, payload),
  submitChange: (id, payload) => api.post(`/ncf/${id}/updates/submit`, payload),
  notifyHr: (id) => api.post(`/ncf/${id}/notify-hr`),
  updateStatus: (id, status, note) => api.patch(`/ncf/${id}/status`, { status, note }),
  updateStartDate: (id, desiredStartDate) => api.put(`/ncf/${id}/start-date`, { desiredStartDate }),
  verifyStartDate: (id) => api.post(`/ncf/${id}/verify-start-date`),
  updateContractEndDate: (id, contractEndDate, reason, noStart = false) => api.put(`/ncf/${id}/contract-end-date`, { contractEndDate, reason, noStart }),
  addDocument: (id, file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    return api.post(`/ncf/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  retrieveJobDivaResume: (id) => api.post(`/ncf/${id}/documents/jobdiva-resume`),
  generate: (id) => api.post(`/ncf/${id}/generate`),
  // Blob response so the browser can save the .xlsx as a real file.
  download: (id, documentId) => api.get(`/ncf/${id}/documents/${documentId}/download`, {
    responseType: 'blob'
  }),
  deleteDocument: (id, documentId) => api.delete(`/ncf/${id}/documents/${documentId}`),
  share: (id, payload) => api.post(`/ncf/${id}/share`, payload),
  remove: (id) => api.delete(`/ncf/${id}`),
  searchJobs: (q, limit = 15, config = {}) => api.get('/ncf/lookup/jobs', { ...config, params: { q, limit } }),
  searchCandidates: (q, limit = 15, config = {}) => api.get('/ncf/lookup/candidates', { ...config, params: { q, limit } }),
  getCandidate: (candidateId) => api.get(`/ncf/lookup/candidates/${candidateId}`),
  getAdminCandidateRaw: (candidateId) => api.get(`/admin/jobdiva/candidates/${candidateId}/raw`),
  getJobCandidates: (jobId, limit = 25) => api.get(`/ncf/lookup/jobs/${jobId}/candidates`, { params: { limit } }),
  getCandidateJobs: (candidateId, limit = 25) => api.get(`/ncf/lookup/candidates/${candidateId}/jobs`, { params: { limit } })
};

export const ncfTemplatesAPI = {
  list: () => api.get('/ncf/templates'),
  getLayout: (templateId) => api.get(`/ncf/templates/${templateId}/layout`),
  saveFieldMap: (templateId, fieldMap) => api.put(`/ncf/templates/${templateId}/field-map`, { fieldMap }),
  activate: (templateId) => api.post(`/ncf/templates/${templateId}/activate`),
  upload: (file, meta = {}) => {
    const formData = new FormData();
    formData.append('template', file);
    if (meta.name) formData.append('name', meta.name);
    if (meta.versionLabel) formData.append('versionLabel', meta.versionLabel);
    return api.post('/ncf/templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const billingAPI = {
  getInvoices: (params = {}, options = {}) => api.get('/billing/invoices', { ...options, params }),
  getStatus: () => api.get('/billing/status'),
  getIndexFolders: () => api.get('/billing/folders'),
  connect: (options = {}) => api.post('/billing/connect', options),
  sync: (options = {}) => api.post('/billing/sync', options),
  stopIndexing: () => api.post('/billing/stop'),
  clearIndex: () => api.post('/billing/clear', { confirm: 'CLEAR_INDEX' }),
  downloadInvoice: (itemId, snapshotId) => api.get(`/billing/invoices/${encodeURIComponent(itemId)}/download`, { responseType: 'blob', params: { snapshotId } }),
  downloadZip: (itemIds, snapshotId) => api.post('/billing/zip', { itemIds, snapshotId }, { responseType: 'blob', timeout: 10 * 60 * 1000 }),
  emailZip: (payload) => api.post('/billing/email', payload, { timeout: 10 * 60 * 1000 })
};

export default api;
